import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import MerchantSearchSelect from '../ui/merchant-search-select';
import MultiSelect from '../ui/multi-select';
import 'flag-icons/css/flag-icons.min.css';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Filter,
  X,
  RefreshCw,
  TrendingDown,
  BarChart3,
  ArrowRight,
  ChevronDown,
  ShoppingCart,
  CreditCard,
  Repeat,
  Search,
  Download,
  TrendingUp,
  Users,
  FileText
} from 'lucide-react';
import { FunnelChart, Funnel, ResponsiveContainer, Tooltip, Cell, LabelList, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, AreaChart, Area, LineChart, Line, ComposedChart, ReferenceLine } from 'recharts';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { useReference } from '../../contexts/ReferenceContext';
import { formatNumberForOverview, formatCurrencySelector, getCurrencySymbol, isSpecificCurrency } from '../../utils/currencyFormatter';
import { getCountryName } from '../../utils/countries';
import AnalyticsCustomers from './AnalyticsCustomers';

// Red palette for funnel - showing transaction attrition
const FUNNEL_COLORS = [
  '#6366f1', // Indigo - Total Transactions (starting point)
  '#f97316', // Orange - After Risk Check (risk declined stage)
  '#ef4444', // Red - After 3DS (3DS declined stage)
  '#991b1b', // Dark Red - After Processor Declined
  '#10b981', // Emerald - Approved (success)
];

// Distinct line colors for decline trends chart (no greens - reserved for approved)
const DECLINE_LINE_COLORS = [
  '#ef4444', '#f97316', '#6366f1', '#ec4899', '#8b5cf6',
  '#f59e0b', '#3b82f6', '#e11d48', '#06b6d4', '#a855f7',
  '#d97706', '#64748b', '#dc2626', '#7c3aed', '#0891b2',
];
const APPROVED_COLOR = '#10b981';

// Get color based on stage - distinct colors for each decline type
const getStageColor = (stage, index) => {
  if (stage.stage_type === 'approved') return '#10b981'; // Green for approved
  if (index === 0) return '#6366f1'; // Indigo for total
  // Distinct colors for each declining stage
  const stageColors = ['#f97316', '#ef4444', '#991b1b']; // Orange, Red, Dark Red
  return stageColors[Math.min(index - 1, stageColors.length - 1)];
};

const DECLINED_COLOR = '#ef4444'; // Red for declined items

// Helper to get days from period
const getDaysFromPeriod = (period, customMonth = null, customYear = null) => {
  const now = new Date();
  
  switch (period) {
    case 'today':
      return 1;
    case 'last7days':
      return 7;
    case 'thismonth':
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const diffTime = now - startOfMonth;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
    case 'thisyear':
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      return Math.ceil((now - startOfYear) / (1000 * 60 * 60 * 24)) || 1;
    case 'lastmonth':
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return Math.ceil((lastMonthEnd - lastMonthStart) / (1000 * 60 * 60 * 24)) + 1;
    case 'custom':
      if (customMonth && customYear) {
        const customMonthStart = new Date(customYear, customMonth - 1, 1);
        const customMonthEnd = new Date(customYear, customMonth, 0);
        return Math.ceil((customMonthEnd - customMonthStart) / (1000 * 60 * 60 * 24)) + 1;
      }
      return 30;
    default:
      return 30;
  }
};

// Custom Tooltip for Funnel - shows surviving count and what was declined
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isApproved = data.stage_type === 'approved';
    
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-100">
        <p className={`font-semibold mb-2 ${isApproved ? 'text-green-600' : 'text-gray-900'}`}>
          {data.name}
        </p>
        <div className="space-y-1 text-sm">
          <p className="text-gray-600">
            Surviving: <span className="font-medium text-gray-900">{formatNumberForOverview(data.value)}</span>
          </p>
          <p className="text-gray-600">
            % of Total: <span className="font-medium text-gray-900">{data.percentage}%</span>
          </p>
          {data.declined > 0 && (
            <p className="text-red-600">
              {data.declined_label}: <span className="font-medium">-{formatNumberForOverview(data.declined)}</span>
            </p>
          )}
        </div>
      </div>
    );
  }
  return null;
};

// Funnel Stage Card Component - Shows surviving transactions at each level
const FunnelStageCard = ({ stage, index, total }) => {
  const isLast = index === total - 1;
  const color = getStageColor(stage, index);
  const isApproved = stage.stage_type === 'approved';
  
  return (
    <div className="flex items-center">
      <div 
        className={`flex-1 p-4 rounded-xl transition-all hover:shadow-md`}
        style={{ 
          background: isApproved ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' :
                     `linear-gradient(135deg, ${color}15, ${color}05)`,
          borderLeft: `4px solid ${color}`
        }}
      >
        <div className="flex justify-between items-start mb-2">
          <span className={`text-sm font-medium ${isApproved ? 'text-green-700' : 'text-gray-600'}`}>
            {stage.name}
          </span>
          <Badge 
            variant="outline" 
            style={{ 
              borderColor: color,
              color: color,
              backgroundColor: `${color}10`
            }}
          >
            {stage.percentage}%
          </Badge>
        </div>
        <p className={`text-2xl font-bold ${isApproved ? 'text-green-600' : 'text-gray-900'}`}>
          {formatNumberForOverview(stage.value)}
        </p>
        {stage.declined > 0 && (
          <div className="mt-2 flex items-center gap-1.5 text-sm bg-red-100 text-red-700 font-semibold px-2 py-1 rounded-md w-fit">
            <TrendingDown className="h-4 w-4 text-red-600" />
            <span>{stage.declined_label}: -{formatNumberForOverview(stage.declined)}</span>
          </div>
        )}
      </div>
      {!isLast && (
        <div className="px-2">
          <ArrowRight className="h-5 w-5 text-gray-300" />
        </div>
      )}
    </div>
  );
};

// Analytics channel filtering is DB-driven: `channelFilter` is an array of real
// channel codes from the Channels settings table (empty = all channels).

// Stable formatter functions for Recharts (prevents re-render on scroll)
const xTickFormatter = (d) => {
  const dt = new Date(d + 'T00:00:00');
  return `${dt.getDate()}/${dt.getMonth() + 1}`;
};
const yTickFormatter = (v) => `${v}%`;
const tooltipFormatter = (value, name) => [`${value}%`, name];
const tooltipLabelFormatter = (d) => {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const APPROVED_CODES = ['00', '00-Approved', '00-Aprobada', '10', '10-Approved', '10-Approved for partial amount'];
const xTickStyle = { fontSize: 11 };
const yTickStyle = { fontSize: 11 };

// Memoized chart component to prevent re-renders on scroll
const DeclineTrendsChart = React.memo(({ declineData, showApproved, showDeclinedSum, selectedCodes }) => {
  // When "Show Declined Sum" is checked, we render only 1 declined-sum line
  // (optionally with 00-Approved if that checkbox is also on).
  const data = useMemo(() => {
    if (!showDeclinedSum) return declineData.daily_data;
    const declineCodes = (declineData.result_codes || []).filter(c => !APPROVED_CODES.includes(c));
    return (declineData.daily_data || []).map(row => {
      const declinedSum = declineCodes.reduce((s, c) => s + (row[c] || 0), 0);
      return { ...row, __declined_sum: Math.round(declinedSum * 100) / 100 };
    });
  }, [declineData, showDeclinedSum]);

  const lines = useMemo(() => {
    if (showDeclinedSum) {
      const out = [];
      if (showApproved) {
        const approvedCode = (declineData.result_codes || []).find(c => APPROVED_CODES.includes(c));
        if (approvedCode) {
          out.push({ code: approvedCode, label: '00: Approved', color: APPROVED_COLOR, isApproved: true });
        }
      }
      out.push({ code: '__declined_sum', label: 'All Declined (sum)', color: DECLINE_LINE_COLORS[0], isApproved: false });
      return out;
    }
    return (declineData.result_codes || [])
      .filter(code => {
        if (APPROVED_CODES.includes(code)) return showApproved;
        return selectedCodes.length === 0 || selectedCodes.includes(code);
      })
      .sort((a, b) => {
        const aSum = declineData.daily_data.reduce((s, d) => s + (d[a] || 0), 0);
        const bSum = declineData.daily_data.reduce((s, d) => s + (d[b] || 0), 0);
        return bSum - aSum;
      })
      .slice(0, 12)
      .map((code, idx) => {
        const desc = declineData.summary?.find(s => s.result_code === code)?.description;
        const label = desc ? `${code}: ${desc}` : code;
        const isApproved = APPROVED_CODES.includes(code);
        const declineIdx = isApproved ? 0 : idx - (showApproved ? 1 : 0);
        const color = isApproved ? APPROVED_COLOR : DECLINE_LINE_COLORS[Math.max(0, declineIdx) % DECLINE_LINE_COLORS.length];
        return { code, label, color, isApproved };
      });
  }, [declineData, showApproved, showDeclinedSum, selectedCodes]);

  return (
    <div className="h-96">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" tick={xTickStyle} tickFormatter={xTickFormatter} />
          <YAxis tick={yTickStyle} tickFormatter={yTickFormatter} />
          <Tooltip formatter={tooltipFormatter} labelFormatter={tooltipLabelFormatter} />
          <Legend />
          {lines.map(({ code, label, color, isApproved }) => (
            <Line
              key={code}
              type="monotone"
              dataKey={code}
              name={label}
              stroke={color}
              strokeWidth={isApproved || code === '__declined_sum' ? 3 : 2}
              dot={false}
              activeDot={{ r: 4 }}
              strokeDasharray={isApproved ? '6 3' : undefined}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

const Analytics = () => {
  const { token, backendUrl } = useAuth();
  const reference = useReference();
  const { selectedPlatform, currentPlatform, platforms } = usePlatform();
  
  const [loading, setLoading] = useState(false);
  const [funnelData, setFunnelData] = useState([]);
  const [orderSuccessData, setOrderSuccessData] = useState(null);
  const [channelFilter, setChannelFilter] = useState([]); // real channel codes from the Channels table; empty = all
  const [mainTab, setMainTab] = useState('approval_rates'); // Main tab: approval_rates or merchant_growth
  const [merchantsByMonth, setMerchantsByMonth] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [topNewMerchants, setTopNewMerchants] = useState([]);
  const [topInactiveMerchants, setTopInactiveMerchants] = useState([]);
  // Sort selector for the Top New / Top Inactive merchant tables
  const [newMerchantSort, setNewMerchantSort] = useState('revenue');
  const [inactiveMerchantSort, setInactiveMerchantSort] = useState('last_revenue');

  // Merchant Pareto state
  const [paretoData, setParetoData] = useState(null);
  const [paretoLoading, setParetoLoading] = useState(false);
  const [paretoMetric, setParetoMetric] = useState('revenue'); // 'revenue' | 'transactions'

  // Sorted views — desc by selected metric, falling back to revenue if missing.
  // Backend returns a wider set (top 30 by revenue); we re-sort and slice to 10.
  const sortedTopNew = useMemo(() => {
    return [...topNewMerchants]
      .sort((a, b) => (b[newMerchantSort] || 0) - (a[newMerchantSort] || 0))
      .slice(0, 10);
  }, [topNewMerchants, newMerchantSort]);
  const sortedTopInactive = useMemo(() => {
    return [...topInactiveMerchants]
      .sort((a, b) => (b[inactiveMerchantSort] || 0) - (a[inactiveMerchantSort] || 0))
      .slice(0, 10);
  }, [topInactiveMerchants, inactiveMerchantSort]);

  // Build the Pareto chart rows (bar value + cumulative %) for the selected metric.
  const paretoChartData = useMemo(() => {
    const block = paretoData?.[paretoMetric];
    if (!block || !block.items) return [];
    return block.items.map((it) => ({
      merchant_id: it.merchant_id,
      name: it.merchant_name,
      label: it.merchant_id === '__others__'
        ? 'Others'
        : (it.merchant_name.length > 14 ? it.merchant_name.slice(0, 13) + '…' : it.merchant_name),
      value: it.value,
      pct: it.pct,
      cumulative_pct: it.cumulative_pct,
    }));
  }, [paretoData, paretoMetric]);

  // Decline trends state
  const [declineData, setDeclineData] = useState(null);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [showApproved, setShowApproved] = useState(true);
  const [showDeclinedSum, setShowDeclinedSum] = useState(true);
  const [selectedCodes, setSelectedCodes] = useState([]); // empty = show all (top 12)

  // Retry Waterfall state
  const [retryData, setRetryData] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    period: 'thismonth',
    country: 'all',
    currency: 'all',
    processor: 'all',
    merchant_id: 'all',
    customMonth: null,
    customYear: null
  });

  // Currency symbol for the Merchants tab money columns (native when a specific
  // currency is selected, else USD-equivalent).
  const merchantsCurrencySym = getCurrencySymbol(isSpecificCurrency(filters.currency) ? filters.currency : 'USD');

  // Default the currency filter to the platform's configured default currency.
  // Re-applies whenever the platform (or its default) changes; user can still
  // switch to "All Currencies" or any other afterwards. Shared with the
  // Recurring Customers tab (AnalyticsCustomers) which reads the same filters.
  const platformDefaultCurrency = (selectedPlatform && selectedPlatform !== 'all')
    ? (currentPlatform?.default_currency || 'all')
    : 'all';
  useEffect(() => {
    if (!currentPlatform) return;
    setFilters(prev => ({ ...prev, currency: platformDefaultCurrency }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, platformDefaultCurrency]);
  
  // Merchant table filters
  const [merchantTableFilters, setMerchantTableFilters] = useState({
    searchText: '',
    minRate: '',
    maxRate: ''
  });
  // Pagination for the merchant approval-rate breakdown table (50 rows/page)
  const MERCHANT_FUNNEL_PAGE_SIZE = 50;
  const [merchantFunnelPage, setMerchantFunnelPage] = useState(1);
  
  // Export loading state
  const [isExporting, setIsExporting] = useState(false);
  // Container ref used to capture the rendered tab content for the PDF export
  const pdfContentRef = useRef(null);
  
  // Available filter options
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [merchantFunnelData, setMerchantFunnelData] = useState([]);

  // Filtered + paginated views of the merchant approval-rate breakdown table
  const filteredMerchantFunnel = useMemo(() => {
    return merchantFunnelData.filter(merchant => {
      const matchesSearch = !merchantTableFilters.searchText ||
        merchant.merchant_name?.toLowerCase().includes(merchantTableFilters.searchText.toLowerCase());
      const matchesMinRate = !merchantTableFilters.minRate ||
        merchant.approval_rate >= parseFloat(merchantTableFilters.minRate);
      const matchesMaxRate = !merchantTableFilters.maxRate ||
        merchant.approval_rate <= parseFloat(merchantTableFilters.maxRate);
      return matchesSearch && matchesMinRate && matchesMaxRate;
    });
  }, [merchantFunnelData, merchantTableFilters]);
  const merchantFunnelTotalPages = Math.max(1, Math.ceil(filteredMerchantFunnel.length / MERCHANT_FUNNEL_PAGE_SIZE));
  const paginatedMerchantFunnel = useMemo(() => {
    const start = (merchantFunnelPage - 1) * MERCHANT_FUNNEL_PAGE_SIZE;
    return filteredMerchantFunnel.slice(start, start + MERCHANT_FUNNEL_PAGE_SIZE);
  }, [filteredMerchantFunnel, merchantFunnelPage]);
  // Reset to page 1 when the table filters or underlying data change
  useEffect(() => {
    setMerchantFunnelPage(1);
  }, [merchantTableFilters, merchantFunnelData]);

  // Currency options for the filter dropdowns. Always include the currently
  // selected currency (e.g. the platform's default) so the SelectTrigger never
  // renders blank when the default isn't present in the data-derived list.
  const currencyFilterOptions = Array.from(new Set([
    ...(currencies || []).filter(c => c && typeof c === 'string'),
    ...((filters.currency && filters.currency !== 'all') ? [filters.currency] : []),
  ]));

  const API = `${backendUrl}/api`;

  // Export merchant funnel data to CSV
  const exportMerchantFunnel = async () => {
    setIsExporting(true);
    
    // Small delay to ensure the loading overlay renders
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      // Get filtered data
      const filteredData = merchantFunnelData.filter(merchant => {
        const matchesSearch = !merchantTableFilters.searchText || 
          merchant.merchant_name?.toLowerCase().includes(merchantTableFilters.searchText.toLowerCase());
        const matchesMinRate = !merchantTableFilters.minRate || 
          merchant.approval_rate >= parseFloat(merchantTableFilters.minRate);
        const matchesMaxRate = !merchantTableFilters.maxRate || 
          merchant.approval_rate <= parseFloat(merchantTableFilters.maxRate);
        return matchesSearch && matchesMinRate && matchesMaxRate;
      });

      if (filteredData.length === 0) {
        alert('No data to export');
        setIsExporting(false);
        return;
      }

      // Define CSV headers
      const headers = [
        'Merchant ID',
        'Merchant Name',
        'Total Transactions',
        'Risk Declined',
        'Risk Rate %',
        '3DS Declined',
        '3DS Rate %',
        'Processor Declined',
        'Processor Rate %',
        'Approved',
        'Approval Rate %',
        'Total Unique Orders',
        'Approved Unique Orders',
        'Order Success Rate %',
        'Total Revenue',
        'Approved Revenue'
      ];

      // Map data to CSV rows
      const rows = filteredData.map(m => [
        m.merchant_id,
        m.merchant_name,
        m.total_transactions,
        m.risk_declined,
        m.risk_rate,
        m.threeds_declined,
        m.threeds_rate,
        m.processor_declined,
        m.processor_rate,
        m.approved,
        m.approval_rate,
        m.total_unique_orders || 0,
        m.approved_unique_orders || 0,
        m.order_success_rate || 0,
        m.total_revenue,
        m.approved_revenue
      ]);

      // Create CSV content
      const escapeCsvValue = (value) => {
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const csvContent = [
        headers.map(escapeCsvValue).join(','),
        ...rows.map(row => row.map(escapeCsvValue).join(','))
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `merchant_funnel_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Export all Analytics tabs to a PDF, captured exactly as rendered on screen (WYSIWYG)
  const [exportProgress, setExportProgress] = useState('');
  const exportAnalyticsPdf = async () => {
    const prevTab = mainTab;
    setIsExporting(true);
    setExportProgress('Preparing report…');
    // small delay so the overlay paints before we start switching tabs
    await new Promise(resolve => setTimeout(resolve, 200));
    try {
      const [{ jsPDF }, html2canvasMod] = await Promise.all([
        import('jspdf'),
        import('html2canvas-pro'),
      ]);
      const html2canvas = html2canvasMod.default;

      const tabsToExport = [
        { id: 'approval_rates', label: 'Approval Rates' },
        { id: 'declined_reasons', label: 'Declined Reasons' },
        { id: 'retry_waterfall', label: 'Retry Waterfall' },
        { id: 'customers', label: 'Customers' },
        { id: 'merchant_growth', label: 'Merchants' },
      ];

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 6;
      const usableW = pageW - margin * 2;

      // True while the active tab is still fetching/recomputing — detects chart
      // spinners (.animate-spin) and the recurring-customers "Applying filters…"
      // overlay. Scoped to the PDF capture container.
      const isTabLoading = () => {
        const el = pdfContentRef.current;
        return !!(el && el.querySelector('.animate-spin, [data-testid="rc-loading-overlay"]'));
      };

      // Wait until the active tab's data has fully loaded AND charts are painted.
      // Production datasets can be slow, so we POLL until loading indicators clear
      // (up to maxMs) rather than giving up after a fixed short timeout — this was
      // the cause of half-loaded tabs (e.g. "Recomputing recurring-customer
      // analytics") being captured into the PDF.
      const waitForTabReady = async (label = '', maxMs = 60000) => {
        // 1) Let React mount the tab and kick off its fetch(es).
        await new Promise(r => setTimeout(r, 600));
        // 2) Let a loading indicator appear first, so we don't race a fetch that
        //    hasn't started yet (which would look "ready" but be empty).
        const appearDeadline = Date.now() + 1500;
        while (Date.now() < appearDeadline && !isTabLoading()) {
          await new Promise(r => setTimeout(r, 100));
        }
        // 3) Wait for loading to clear; require it to STAY clear for ~750ms to
        //    absorb brief gaps between a tab's sequential fetches.
        const deadline = Date.now() + maxMs;
        let stableClears = 0;
        while (Date.now() < deadline) {
          if (isTabLoading()) {
            stableClears = 0;
            if (label) setExportProgress(`Loading ${label} data…`);
            await new Promise(r => setTimeout(r, 250));
          } else if (++stableClears >= 3) {
            break;
          } else {
            await new Promise(r => setTimeout(r, 250));
          }
        }
        // 4) Settle final layout (chart animations are disabled during export).
        await new Promise(r => setTimeout(r, 900));
      };

      let firstPage = true;

      // Resolve the platform name to print at the top of page 1.
      const platformDisplayName = (!selectedPlatform || selectedPlatform === 'all')
        ? 'All Platforms'
        : (platforms.find(p => p.id === selectedPlatform)?.name || currentPlatform?.name || 'Platform');

      // Draws the report header (platform name + date) on the current page.
      // Returns the y-coordinate (mm) where content should start below it.
      const drawReportHeader = () => {
        const top = margin;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(15);
        pdf.setTextColor(30, 30, 30);
        pdf.text(platformDisplayName, margin, top + 5);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.text(`Analytics Report · Generated ${new Date().toLocaleDateString()}`, margin, top + 10);
        pdf.setDrawColor(220, 220, 220);
        pdf.line(margin, top + 12.5, pageW - margin, top + 12.5);
        return top + 15;
      };

      for (let i = 0; i < tabsToExport.length; i++) {
        const { id, label } = tabsToExport[i];
        setExportProgress(`Rendering ${label} (${i + 1}/${tabsToExport.length})…`);
        setMainTab(id);
        await waitForTabReady(label);

        const el = pdfContentRef.current;
        if (!el) continue;
        // Capture the FULL rendered size (scrollWidth/Height) so nothing is clipped
        // on the right/bottom. windowWidth/Height must match the capture width/height,
        // otherwise content rendered wider than the output canvas gets cut off.
        const captureW = el.scrollWidth;
        const captureH = el.scrollHeight;
        const canvas = await html2canvas(el, {
          scale: 1.35,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: captureW,
          height: captureH,
          windowWidth: captureW,
          windowHeight: captureH,
          scrollX: 0,
          scrollY: 0,
          imageTimeout: 8000,
          ignoreElements: (node) => {
            // Skip zero-size elements that can crash the canvas renderer
            try {
              if (node.nodeType !== 1) return false;
              const tag = node.tagName;
              if (tag === 'CANVAS' && (node.width === 0 || node.height === 0)) return true;
              if ((tag === 'IMG' || tag === 'CANVAS' || tag === 'svg') &&
                  node.offsetParent !== null &&
                  node.offsetWidth === 0 && node.offsetHeight === 0) return true;
            } catch (e) { /* ignore */ }
            return false;
          },
        });

        const imgH = (canvas.height * usableW) / canvas.width;
        const imgData = canvas.toDataURL('image/jpeg', 0.92);

        if (!firstPage) pdf.addPage();

        // On the very first page, print the report header (platform name + date)
        // and start the captured image below it.
        let firstPageTop = margin;
        if (firstPage) {
          firstPageTop = drawReportHeader();
        }
        firstPage = false;

        // paginate a tall capture across multiple PDF pages; the first slice
        // begins below the header on page 1, full-page from page 2 onwards.
        let pageTop = firstPageTop;
        let consumed = 0;
        while (consumed < imgH - 0.5) {
          const avail = pageH - pageTop - margin;
          pdf.addImage(imgData, 'JPEG', margin, pageTop - consumed, usableW, imgH);
          consumed += avail;
          if (consumed < imgH - 0.5) {
            pdf.addPage();
            pageTop = margin;
          }
        }
      }

      setExportProgress('Saving PDF…');
      // Download via the same blob + anchor mechanism used by the Excel/CSV exports
      const pdfBlob = pdf.output('blob');
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('PDF export failed. Please try again.');
    } finally {
      setMainTab(prevTab);
      setExportProgress('');
      setIsExporting(false);
    }
  };

  // Fetch filter options — country list is data-driven (only countries present in
  // the data); currencies/processors come from the DB Settings tables.
  const fetchFilterOptions = useCallback(async () => {
    if (!token) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const platformParam = selectedPlatform || 'all';
      const [countriesRes, merchantsRes] = await Promise.all([
        axios.get(`${API}/filters/countries?platform_id=${platformParam}`, { headers }),
        axios.get(`${API}/filters/merchants?platform_id=${platformParam}`, { headers })
      ]);
      setCountries(countriesRes.data.countries || []);
      setMerchants(merchantsRes.data.merchants || []);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  }, [token, selectedPlatform, API]);

  // Keep currency/processor filter lists in sync with the DB Settings tables.
  useEffect(() => {
    setCurrencies((reference.currencies || []).map(c => c.code));
    setProcessors((reference.processors || []).map(p => p.code));
  }, [reference.currencies, reference.processors]);

  // Fetch funnel data
  const fetchFunnelData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    
    try {
      const days = getDaysFromPeriod(filters.period, filters.customMonth, filters.customYear);
      
      const params = new URLSearchParams({
        platform_id: selectedPlatform || 'all',
        period: filters.period,
        days: days.toString()
      });
      
      if (filters.country && filters.country !== 'all') {
        params.append('country', filters.country);
      }
      if (filters.currency && filters.currency !== 'all') {
        params.append('currency', filters.currency);
      }
      if (filters.processor && filters.processor !== 'all') {
        params.append('processor', filters.processor);
      }
      if (filters.merchant_id && filters.merchant_id !== 'all') {
        params.append('merchant_id', filters.merchant_id);
      }
      
      // Channel filter from the DB-driven channel multi-select (empty = all channels)
      channelFilter.forEach(ch => params.append('channel', ch));
      
      if (filters.period === 'custom' && filters.customMonth && filters.customYear) {
        const startDate = `${filters.customYear}-${String(filters.customMonth).padStart(2, '0')}-01`;
        params.append('start_date', startDate);
      } else if (filters.period === 'lastmonth') {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-01`;
        params.append('start_date', startDate);
      }
      
      const response = await axios.get(`${API}/analytics/payment-funnel?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFunnelData(response.data.funnel || []);
      setOrderSuccessData(response.data.order_success || null);
      
      // Also fetch merchant funnel data with all filters
      const merchantParams = new URLSearchParams({
        platform_id: selectedPlatform || 'all',
        period: filters.period,
        days: days.toString(),
        limit: '10000'  // Show all merchants (no practical limit)
      });
      
      // Apply all the same filters as the main funnel
      if (filters.country && filters.country !== 'all') {
        merchantParams.append('country', filters.country);
      }
      if (filters.currency && filters.currency !== 'all') {
        merchantParams.append('currency', filters.currency);
      }
      if (filters.processor && filters.processor !== 'all') {
        merchantParams.append('processor', filters.processor);
      }
      if (filters.merchant_id && filters.merchant_id !== 'all') {
        merchantParams.append('merchant_id', filters.merchant_id);
      }
      
      // Add channel filter to merchant funnel as well (DB-driven; empty = all)
      channelFilter.forEach(ch => merchantParams.append('channel', ch));
      
      if (filters.period === 'custom' && filters.customMonth && filters.customYear) {
        const startDate = `${filters.customYear}-${String(filters.customMonth).padStart(2, '0')}-01`;
        merchantParams.append('start_date', startDate);
      } else if (filters.period === 'lastmonth') {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-01`;
        merchantParams.append('start_date', startDate);
      }
      
      const merchantResponse = await axios.get(`${API}/analytics/merchant-funnel?${merchantParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMerchantFunnelData(merchantResponse.data.merchants || []);
    } catch (err) {
      console.error('Error fetching funnel data:', err);
      setFunnelData([]);
      setOrderSuccessData(null);
      setMerchantFunnelData([]);
    } finally {
      setLoading(false);
    }
  }, [token, selectedPlatform, filters, channelFilter, API]);

  // Fetch merchants by month data (respects country/currency/processor/merchant filters)
  const fetchMerchantsByMonth = useCallback(async () => {
    if (!token) return;
    
    setMerchantsLoading(true);
    try {
      const params = new URLSearchParams();
      // Only pass platform_id if it's a specific platform, not "all"
      if (selectedPlatform && selectedPlatform !== 'all') {
        params.append('platform_id', selectedPlatform);
      }
      if (filters.country && filters.country !== 'all') params.append('country', filters.country);
      if (filters.currency && filters.currency !== 'all') params.append('currency', filters.currency);
      if (filters.processor && filters.processor !== 'all') params.append('processor', filters.processor);
      if (filters.merchant_id && filters.merchant_id !== 'all') params.append('merchant_id', filters.merchant_id);
      
      const response = await axios.get(`${API}/analytics/merchants-by-month?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMerchantsByMonth(response.data.data || []);
      setTopNewMerchants(response.data.top_new_merchants || []);
      setTopInactiveMerchants(response.data.top_inactive_merchants || []);
    } catch (err) {
      console.error('Error fetching merchants by month:', err);
      setMerchantsByMonth([]);
      setTopNewMerchants([]);
      setTopInactiveMerchants([]);
    } finally {
      setMerchantsLoading(false);
    }
  }, [token, selectedPlatform, filters, API]);

  // Load merchants data when on merchant growth tab
  useEffect(() => {
    if (mainTab === 'merchant_growth' && token) {
      fetchMerchantsByMonth();
    }
  }, [mainTab, token, fetchMerchantsByMonth]);

  // Fetch merchant Pareto data (respects the same filters as the funnel)
  const fetchMerchantPareto = useCallback(async () => {
    if (!token) return;
    setParetoLoading(true);
    try {
      const days = getDaysFromPeriod(filters.period, filters.customMonth, filters.customYear);
      const params = new URLSearchParams({
        platform_id: selectedPlatform || 'all',
        period: filters.period,
        days: days.toString(),
        top_n: '20',
      });
      if (filters.country && filters.country !== 'all') params.append('country', filters.country);
      if (filters.currency && filters.currency !== 'all') params.append('currency', filters.currency);
      if (filters.processor && filters.processor !== 'all') params.append('processor', filters.processor);
      if (filters.merchant_id && filters.merchant_id !== 'all') params.append('merchant_id', filters.merchant_id);
      if (filters.period === 'custom' && filters.customMonth && filters.customYear) {
        params.append('start_date', `${filters.customYear}-${String(filters.customMonth).padStart(2, '0')}-01`);
      } else if (filters.period === 'lastmonth') {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        params.append('start_date', `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-01`);
      }
      const response = await axios.get(`${API}/analytics/merchant-pareto?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParetoData(response.data);
    } catch (err) {
      console.error('Error fetching merchant pareto:', err);
      setParetoData(null);
    } finally {
      setParetoLoading(false);
    }
  }, [token, selectedPlatform, filters, API]);

  // Load Pareto data when on merchant growth tab (and when filters change)
  useEffect(() => {
    if (mainTab === 'merchant_growth' && token) {
      fetchMerchantPareto();
    }
  }, [mainTab, token, fetchMerchantPareto]);

  // Fetch decline trends data
  const fetchDeclineTrends = useCallback(async () => {
    if (!token || !selectedPlatform) return;
    setDeclineLoading(true);
    try {
      const params = new URLSearchParams({
        platform_id: selectedPlatform,
        period: filters.period,
      });
      if (filters.country !== 'all') params.append('country', filters.country);
      if (filters.currency !== 'all') params.append('currency', filters.currency);
      if (filters.processor !== 'all') params.append('processor', filters.processor);
      if (filters.merchant_id !== 'all') params.append('merchant_id', filters.merchant_id);
      // Channel filter from the DB-driven channel multi-select (empty = all channels)
      channelFilter.forEach(c => params.append('channel', c));
      if (filters.period === 'custom' && filters.customMonth) {
        params.append('custom_month', filters.customMonth);
        params.append('custom_year', filters.customYear || new Date().getFullYear());
      }
      
      const response = await axios.get(`${API}/analytics/decline-trends?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeclineData(response.data);
      setSelectedCodes([]); // Reset selection on new data
    } catch (err) {
      console.error('Error fetching decline trends:', err);
      setDeclineData(null);
    } finally {
      setDeclineLoading(false);
    }
  }, [token, selectedPlatform, filters, channelFilter, API]);

  useEffect(() => {
    if (mainTab === 'declined_reasons' && token) {
      fetchDeclineTrends();
    }
  }, [mainTab, token, fetchDeclineTrends]);

  // Fetch retry waterfall data
  const fetchRetryWaterfall = useCallback(async () => {
    if (!token) return;
    setRetryLoading(true);
    try {
      const days = getDaysFromPeriod(filters.period, filters.customMonth, filters.customYear);
      const params = new URLSearchParams({
        platform_id: selectedPlatform || 'all',
        period: filters.period,
        days: days.toString(),
      });
      if (filters.country && filters.country !== 'all') params.append('country', filters.country);
      if (filters.currency && filters.currency !== 'all') params.append('currency', filters.currency);
      if (filters.processor && filters.processor !== 'all') params.append('processor', filters.processor);
      if (filters.merchant_id && filters.merchant_id !== 'all') params.append('merchant_id', filters.merchant_id);

      // Channel filter from the DB-driven channel multi-select (empty = all channels)
      channelFilter.forEach(c => params.append('channel', c));

      if (filters.period === 'custom' && filters.customMonth && filters.customYear) {
        const startDate = `${filters.customYear}-${String(filters.customMonth).padStart(2, '0')}-01`;
        params.append('start_date', startDate);
      } else if (filters.period === 'lastmonth') {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-01`;
        params.append('start_date', startDate);
      }

      const response = await axios.get(`${API}/analytics/retry-waterfall?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRetryData(response.data);
    } catch (err) {
      console.error('Error fetching retry waterfall:', err);
      setRetryData(null);
    } finally {
      setRetryLoading(false);
    }
  }, [token, selectedPlatform, filters, channelFilter, API]);

  useEffect(() => {
    if (mainTab === 'retry_waterfall' && token) {
      fetchRetryWaterfall();
    }
  }, [mainTab, token, fetchRetryWaterfall]);

  // Load data on mount and filter change
  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchFunnelData();
  }, [fetchFunnelData]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      period: 'thismonth',
      country: 'all',
      currency: 'all',
      processor: 'all',
      merchant_id: 'all',
      customMonth: null,
      customYear: null
    });
  };

  // Check if any filters are active
  const hasActiveFilters = filters.country !== 'all' || 
    filters.currency !== 'all' || 
    filters.processor !== 'all' || 
    filters.merchant_id !== 'all';

  // Period options
  const periodOptions = [
    { value: 'today', label: 'Today' },
    { value: 'last7days', label: 'Last 7 days' },
    { value: 'thismonth', label: 'This Month' },
    { value: 'lastmonth', label: 'Last Month' },
    { value: 'thisyear', label: 'This Year' },
    { value: 'custom', label: 'Custom' }
  ];

  // Month options for custom period
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  // Calculate summary stats from the new funnel structure
  const summaryStats = useMemo(() => {
    if (funnelData.length < 2) return null;
    
    // Find stages - funnel now shows surviving counts
    const totalStage = funnelData.find(s => s.name === 'Total Transactions');
    const afterRiskStage = funnelData.find(s => s.name === 'After Risk Check');
    const after3dsStage = funnelData.find(s => s.name === 'After 3DS');
    const approvedStage = funnelData.find(s => s.stage_type === 'approved');
    
    const total = totalStage?.value || 0;
    const afterRisk = afterRiskStage?.value || 0;
    const after3ds = after3dsStage?.value || 0;
    const approved = approvedStage?.value || 0;
    const approvalRate = total > 0 ? ((approved / total) * 100).toFixed(1) : 0;
    
    // Get declined counts from each stage
    const riskDeclined = afterRiskStage?.declined || 0;
    const threedsDeclined = after3dsStage?.declined || 0;
    const processorDeclined = approvedStage?.declined || 0;
    const totalDeclined = riskDeclined + threedsDeclined + processorDeclined;
    
    return {
      total,
      afterRisk,
      after3ds,
      approved,
      approvalRate,
      totalDeclined,
      riskDeclined,
      threedsDeclined,
      processorDeclined
    };
  }, [funnelData]);
  
  // Title/description reflect the DB-driven channel multi-select selection.
  const getTabInfo = () => {
    if (!channelFilter || channelFilter.length === 0) {
      return { title: 'Payment Approval Rates', icon: '📊', description: 'Analyze your payment funnel and conversion rates across all channels' };
    }
    const names = channelFilter.map(c => (reference.channels || []).find(x => x.code === c)?.name || c);
    return { title: `${names.join(', ')} — Approval Rates`, icon: '📊', description: 'Payment funnel and conversion analysis for the selected channel(s)' };
  };

  const tabInfo = getTabInfo();

  // Shared DB-driven channel multi-select (replaces the old ecommerce/POS/subscription tabs).
  const renderChannelMultiSelect = (testid) => (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-600">Channel:</span>
      <MultiSelect
        options={[{ code: 'All', name: 'All Channels' }, ...(reference.channels || [])]}
        value={channelFilter}
        onChange={(value) => {
          if (value.includes('All')) {
            setChannelFilter([]);
          } else {
            setChannelFilter(value);
          }
        }}
        placeholder="All Channels"
        className="w-52"
        maxDisplayItems={2}
        dataTestid={testid}
        optionTestidPrefix="analytics-channel"
        renderOption={(ch) => (ch.code === 'All' ? 'All Channels' : (ch.name || ch.code))}
        renderValue={(ch) => (ch.code === 'All' ? 'All Channels' : (ch.name || ch.code))}
      />
    </div>
  );

  return (
    <div className="space-y-6 p-6">
      {/* Export Loading Overlay */}
      {isExporting && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="export-loading-overlay">
          <div className="bg-white rounded-xl shadow-2xl p-8 flex flex-col items-center gap-4 max-w-sm mx-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-indigo-200 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">Exporting to PDF</h3>
              <p className="text-sm text-gray-500 mt-1" data-testid="export-progress-text">{exportProgress || 'Please wait while we prepare your file...'}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header with Main Tabs */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-indigo-600" />
              Analytics
            </h1>
            <p className="text-gray-500 mt-1">Payment funnel analytics and merchant insights</p>
          </div>
          <Button
            onClick={exportAnalyticsPdf}
            disabled={isExporting}
            className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2 self-start sm:self-auto"
            data-testid="export-analytics-pdf-btn"
          >
            <FileText className="h-4 w-4" />
            Export to PDF
          </Button>
        </div>

        {/* Main Tabs: Approval Rates / Declined Reasons / Retry Waterfall / Merchant Growth / Customers */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="grid w-full max-w-4xl grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 bg-gray-100 h-auto">
            <TabsTrigger value="approval_rates" className="flex items-center gap-2 data-[state=active]:bg-white" data-testid="approval-rates-tab">
              <span className="text-base">📊</span>
              Approval Rates
            </TabsTrigger>
            <TabsTrigger value="declined_reasons" className="flex items-center gap-2 data-[state=active]:bg-white" data-testid="declined-reasons-tab">
              <span className="text-base">📉</span>
              Declined Reasons
            </TabsTrigger>
            <TabsTrigger value="retry_waterfall" className="flex items-center gap-2 data-[state=active]:bg-white" data-testid="retry-waterfall-tab">
              <span className="text-base">🔁</span>
              Retry Waterfall
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex items-center gap-2 data-[state=active]:bg-white" data-testid="customers-tab">
              <span className="text-base">👥</span>
              Customers
            </TabsTrigger>
            <TabsTrigger value="merchant_growth" className="flex items-center gap-2 data-[state=active]:bg-white" data-testid="merchant-growth-tab">
              <span className="text-base">🏪</span>
              Merchants
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* PDF capture container — wraps all tab content so it can be exported as rendered */}
      <div ref={pdfContentRef} className="space-y-6">
      {/* Approval Rates Tab Content */}
      {mainTab === 'approval_rates' && (
        <>
          {/* Channel Tabs Header */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-indigo-600" />
                  {tabInfo.title}
                </h2>
                <p className="text-gray-500 text-sm">{tabInfo.description}</p>
              </div>
            </div>

            {/* Payment Approval Rates header */}
          </div>

      {/* Always Visible Filters */}
      <Card className="border-0 shadow-md bg-white">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Period Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Period:</span>
              <Select value={filters.period} onValueChange={(v) => handleFilterChange('period', v)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {filters.period === 'custom' && (
                <>
                  <Select 
                    value={filters.customMonth?.toString() || ''} 
                    onValueChange={(v) => handleFilterChange('customMonth', parseInt(v))}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select 
                    value={filters.customYear?.toString() || ''} 
                    onValueChange={(v) => handleFilterChange('customYear', parseInt(v))}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>

            {/* Channel Filter (moved next to Period) */}
            {renderChannelMultiSelect('analytics-approval-channel-filter')}

            {/* Country Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Country:</span>
              <Select value={filters.country} onValueChange={(v) => handleFilterChange('country', v)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Countries</SelectItem>
                  {countries.filter(c => c && c.code).map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className={`fi fi-${c.code.toLowerCase()} mr-2`}></span>
                      {c.name || getCountryName(c.code)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Currency Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Currency:</span>
              <Select value={filters.currency} onValueChange={(v) => handleFilterChange('currency', v)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {currencyFilterOptions.map(c => (
                    <SelectItem key={c} value={c}>{formatCurrencySelector(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Processor Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Processor:</span>
              <Select value={filters.processor} onValueChange={(v) => handleFilterChange('processor', v)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {processors.filter(p => p && typeof p === 'string').map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Merchant Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Merchant:</span>
              <MerchantSearchSelect
                merchants={merchants}
                value={filters.merchant_id}
                onChange={(v) => handleFilterChange('merchant_id', v)}
                placeholder="All Merchants"
                className="w-48"
              />
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}

            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={fetchFunnelData}
              disabled={loading}
              className="flex items-center gap-2 ml-auto"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summaryStats && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          <Card className="border-0 shadow-md bg-gradient-to-br from-indigo-50 to-white">
            <CardContent className="p-3">
              <p className="text-xs text-gray-600">Total Transactions</p>
              <p className="text-xl font-bold text-indigo-700">{formatNumberForOverview(summaryStats.total)}</p>
              <p className="text-xs text-indigo-500 mt-1">100%</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-orange-50 to-white">
            <CardContent className="p-3">
              <p className="text-xs text-gray-600">After Risk Check</p>
              <p className="text-xl font-bold text-gray-800">{formatNumberForOverview(summaryStats.afterRisk)}</p>
              <p className="text-xs text-orange-600 mt-1">
                {summaryStats.total > 0 ? ((summaryStats.afterRisk / summaryStats.total) * 100).toFixed(1) : 0}%
                <span className="ml-1 text-red-600 font-semibold">-{formatNumberForOverview(summaryStats.riskDeclined)}</span>
              </p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-red-50 to-white">
            <CardContent className="p-3">
              <p className="text-xs text-gray-600">After 3DS</p>
              <p className="text-xl font-bold text-gray-800">{formatNumberForOverview(summaryStats.after3ds)}</p>
              <p className="text-xs text-red-600 mt-1">
                {summaryStats.total > 0 ? ((summaryStats.after3ds / summaryStats.total) * 100).toFixed(1) : 0}%
                <span className="ml-1 font-semibold">-{formatNumberForOverview(summaryStats.threedsDeclined)}</span>
              </p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-red-100 to-red-50 border-l-4 border-l-red-600">
            <CardContent className="p-3">
              <p className="text-xs text-red-700">After Processor</p>
              <p className="text-xl font-bold text-red-700">{formatNumberForOverview(summaryStats.approved)}</p>
              <p className="text-xs text-red-500 mt-1">
                {summaryStats.total > 0 ? ((summaryStats.approved / summaryStats.total) * 100).toFixed(1) : 0}%
                <span className="ml-1 bg-red-200 text-red-700 px-1 rounded">-{formatNumberForOverview(summaryStats.processorDeclined)}</span>
              </p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-50 to-white border-l-4 border-l-emerald-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-600">Approved</p>
              <p className="text-xl font-bold text-emerald-700">{formatNumberForOverview(summaryStats.approved)}</p>
              <p className="text-xs text-emerald-600 mt-1">{summaryStats.approvalRate}% of total</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-green-100 to-green-50">
            <CardContent className="p-3">
              <p className="text-xs text-gray-600">Approval Rate</p>
              <p className="text-xl font-bold text-green-700">{summaryStats.approvalRate}%</p>
            </CardContent>
          </Card>
          {orderSuccessData && (
            <Card className="border-0 shadow-md bg-gradient-to-br from-blue-100 to-blue-50">
              <CardContent className="p-3">
                <p className="text-xs text-gray-600">Order Success Rate</p>
                <p className="text-xl font-bold text-blue-700">{orderSuccessData.order_success_rate}%</p>
                <p className="text-xs text-blue-600 mt-1">
                  {orderSuccessData.approved_unique_orders.toLocaleString()} / {orderSuccessData.total_unique_orders.toLocaleString()} orders
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Funnel Chart */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ChevronDown className="h-5 w-5 text-indigo-600" />
            Payment Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
                <p className="text-gray-500">Loading funnel data...</p>
              </div>
            </div>
          ) : funnelData.length > 0 ? (
            <div className="flex justify-center">
              {/* Proportional Funnel Visualization */}
              {/* Width = surviving transactions, Height = proportional to DROP at each stage */}
              <div className="h-[500px] w-full max-w-2xl flex flex-col justify-start items-center py-4">
                {(() => {
                  const maxValue = funnelData[0]?.value || 1;
                  const approvedStage = funnelData.find(s => s.stage_type === 'approved');
                  const processorDeclined = approvedStage?.declined || 0;
                  const approvedValue = approvedStage?.value || 0;
                  const approvedPct = (approvedValue / maxValue) * 100;
                  
                  // Build extended funnel data with After Processor Declined as separate layer
                  const extendedFunnel = [];
                  funnelData.forEach((stage, idx) => {
                    if (stage.stage_type === 'approved') {
                      // Add After Processor Declined layer before Approved
                      if (processorDeclined > 0) {
                        extendedFunnel.push({
                          name: 'After Processor Declined',
                          value: processorDeclined,
                          percentage: ((processorDeclined / maxValue) * 100).toFixed(1),
                          isDeclinedLayer: true,
                          prevValue: funnelData[idx - 1]?.value || maxValue
                        });
                      }
                      // Add Approved layer
                      extendedFunnel.push({
                        ...stage,
                        isApprovedLayer: true
                      });
                    } else {
                      extendedFunnel.push(stage);
                    }
                  });
                  
                  // Calculate DROP percentages for each segment
                  // Drop = (previous value - current value) / total * 100
                  // First segment (Total) has no drop, but we give it a base height
                  // Last segment (Approved) is what remains
                  const drops = extendedFunnel.map((stage, index) => {
                    if (index === 0) {
                      // First segment - no drop, give it base height representing 100%
                      return 20; // Base height for total transactions
                    }
                    
                    const prevStage = extendedFunnel[index - 1];
                    const prevValue = stage.isDeclinedLayer ? stage.prevValue : prevStage?.value || maxValue;
                    const currentValue = stage.isDeclinedLayer ? approvedValue : stage.value;
                    
                    // Drop percentage relative to total
                    const dropCount = prevValue - currentValue;
                    const dropPercent = (dropCount / maxValue) * 100;
                    
                    // For approved (last segment), use remaining percentage
                    if (stage.isApprovedLayer) {
                      return Math.max(10, (approvedValue / maxValue) * 100 * 0.3); // Scale down for visual balance
                    }
                    
                    return Math.max(dropPercent, 5); // Minimum 5% for visibility
                  });
                  
                  const totalDrops = drops.reduce((sum, d) => sum + d, 0);
                  
                  return extendedFunnel.map((stage, index) => {
                    const isDeclinedLayer = stage.isDeclinedLayer;
                    const isApprovedLayer = stage.isApprovedLayer;
                    
                    // Width calculation
                    let widthPercent;
                    if (isDeclinedLayer) {
                      // Declined layer width = same as previous surviving stage
                      widthPercent = (stage.prevValue / maxValue) * 100;
                    } else {
                      widthPercent = (stage.value / maxValue) * 100;
                    }
                    
                    // Color
                    let color;
                    if (isDeclinedLayer) {
                      color = '#7f1d1d'; // Very dark red for processor declined
                    } else if (isApprovedLayer) {
                      color = '#10b981'; // Green for approved
                    } else if (index === 0) {
                      color = '#6366f1'; // Indigo for total
                    } else if (stage.name === 'After Risk Check') {
                      color = '#f97316'; // Orange for risk check stage
                    } else if (stage.name === 'After 3DS') {
                      color = '#ef4444'; // Red for 3DS stage
                    } else {
                      color = '#f87171'; // Light red for other intermediate
                    }
                    
                    // Height calculation - proportional to DROP at this stage
                    const dropValue = drops[index];
                    let heightPercent = (dropValue / totalDrops) * 100;
                    
                    // Ensure minimum readable height
                    heightPercent = Math.max(heightPercent, 10);
                    
                    // Get next layer for trapezoid shape
                    const nextStage = extendedFunnel[index + 1];
                    let nextWidthPercent;
                    if (isDeclinedLayer) {
                      // Declined layer narrows to approved width
                      nextWidthPercent = approvedPct;
                    } else if (nextStage?.isDeclinedLayer) {
                      // Before declined layer - same width
                      nextWidthPercent = widthPercent;
                    } else if (nextStage) {
                      nextWidthPercent = (nextStage.value / maxValue) * 100;
                    } else {
                      nextWidthPercent = widthPercent * 0.95;
                    }
                    
                    const leftIndent = Math.max(((widthPercent - nextWidthPercent) / 2) / widthPercent * 100, 0);
                    const rightIndent = Math.min(100 - leftIndent, 100);
                    
                    return (
                      <div 
                        key={stage.name}
                        className="relative flex items-center justify-center transition-all duration-500 hover:opacity-90 hover:scale-[1.01]"
                        style={{
                          width: `${Math.max(widthPercent, 25)}%`,
                          height: `${heightPercent}%`,
                          minHeight: '50px',
                          backgroundColor: color,
                          clipPath: `polygon(0% 0%, 100% 0%, ${rightIndent}% 100%, ${leftIndent}% 100%)`,
                          marginTop: index === 0 ? 0 : '-1px',
                        }}
                      >
                        <div className={`text-center font-semibold text-sm drop-shadow-md z-10 ${isDeclinedLayer ? 'text-white' : 'text-white'}`}>
                          <div className="text-xs opacity-90">{stage.name}</div>
                          {isDeclinedLayer ? (
                            <>
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-base font-bold">{formatNumberForOverview(approvedValue)}</span>
                                <span className="text-xs opacity-80">({((approvedValue / (approvedValue + stage.value)) * 100).toFixed(1)}%)</span>
                                <span className="text-xs bg-black/20 rounded px-1.5 py-0.5">-{stage.value}</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-base font-bold">{formatNumberForOverview(stage.value)}</span>
                                <span className="text-xs opacity-80">({stage.percentage}%)</span>
                                {!isApprovedLayer && index > 0 && stage.declined > 0 && (
                                  <span className="text-xs bg-black/20 rounded px-1.5 py-0.5">-{stage.declined}</span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          ) : (
            <div className="h-80 flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No funnel data available for the selected period</p>
                <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Merchant Funnel Table */}
      {merchantFunnelData.length > 0 && (
        <Card className="border-0 shadow-lg mt-6">
          <CardHeader className="pb-2">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-indigo-600" />
                Funnel by Merchant
                <Badge variant="outline" className="ml-2 text-xs">
                  {(() => {
                    const filtered = merchantFunnelData.filter(m => {
                      const matchesSearch = !merchantTableFilters.searchText || 
                        m.merchant_name?.toLowerCase().includes(merchantTableFilters.searchText.toLowerCase());
                      const matchesMinRate = !merchantTableFilters.minRate || 
                        m.approval_rate >= parseFloat(merchantTableFilters.minRate);
                      const matchesMaxRate = !merchantTableFilters.maxRate || 
                        m.approval_rate <= parseFloat(merchantTableFilters.maxRate);
                      return matchesSearch && matchesMinRate && matchesMaxRate;
                    });
                    return `${filtered.length} of ${merchantFunnelData.length} Merchants`;
                  })()}
                </Badge>
              </CardTitle>
              
              {/* Table Filters */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Merchant Search */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search merchant..."
                    value={merchantTableFilters.searchText}
                    onChange={(e) => setMerchantTableFilters(prev => ({ ...prev, searchText: e.target.value }))}
                    className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md w-48 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    data-testid="merchant-search-input"
                  />
                </div>
                
                {/* Rate Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Rate:</span>
                  <input
                    type="number"
                    placeholder="Min %"
                    value={merchantTableFilters.minRate}
                    onChange={(e) => setMerchantTableFilters(prev => ({ ...prev, minRate: e.target.value }))}
                    className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="0"
                    max="100"
                    data-testid="merchant-min-rate-input"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="Max %"
                    value={merchantTableFilters.maxRate}
                    onChange={(e) => setMerchantTableFilters(prev => ({ ...prev, maxRate: e.target.value }))}
                    className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="0"
                    max="100"
                    data-testid="merchant-max-rate-input"
                  />
                </div>
                
                {/* Clear Filters */}
                {(merchantTableFilters.searchText || merchantTableFilters.minRate || merchantTableFilters.maxRate) && (
                  <button
                    onClick={() => setMerchantTableFilters({ searchText: '', minRate: '', maxRate: '' })}
                    className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    data-testid="clear-merchant-filters"
                  >
                    <X className="h-3 w-3" />
                    Clear
                  </button>
                )}
                
                {/* Export Button */}
                <button
                  onClick={exportMerchantFunnel}
                  disabled={isExporting}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="export-merchant-funnel"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">#</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Merchant</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Total</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">
                      <span className="text-orange-600">Risk</span>
                    </th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">
                      <span className="text-red-600">3DS</span>
                    </th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">
                      <span className="text-red-800">Processor</span>
                    </th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">
                      <span className="text-green-600">Approved</span>
                    </th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">
                      <div className="text-green-600">Approval Rate</div>
                      <div className="text-xs text-gray-400 font-normal">(transactions)</div>
                    </th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">
                      <div className="text-blue-600">Order Success</div>
                      <div className="text-xs text-gray-400 font-normal">(unique orders)</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMerchantFunnel
                    .map((merchant, index) => {
                    const globalIndex = (merchantFunnelPage - 1) * MERCHANT_FUNNEL_PAGE_SIZE + index;
                    const approvalColor = merchant.approval_rate >= 65 ? 'text-green-600' : 
                                          merchant.approval_rate >= 55 ? 'text-yellow-600' : 'text-red-600';
                    const orderSuccessRate = merchant.order_success_rate || 0;
                    const orderSuccessColor = orderSuccessRate >= 70 ? 'text-green-600' : 
                                              orderSuccessRate >= 50 ? 'text-yellow-600' : 'text-red-600';
                    const rowBg = globalIndex % 2 === 0 ? 'bg-gray-50/50' : 'bg-white';
                    
                    return (
                      <tr key={merchant.merchant_id} className={`${rowBg} hover:bg-indigo-50/50 transition-colors border-b border-gray-100`}>
                        <td className="py-3 px-4 text-sm text-gray-500 font-medium">
                          {globalIndex + 1}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900 text-sm">{merchant.merchant_name}</div>
                          <div className="text-xs text-gray-500">${formatNumberForOverview(merchant.approved_revenue)} revenue</div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="font-semibold text-gray-900">{merchant.total_transactions}</div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="font-semibold text-orange-600">-{merchant.risk_declined}</span>
                            <span className="text-xs text-gray-500">{merchant.risk_rate}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="font-semibold text-red-600">-{merchant.threeds_declined}</span>
                            <span className="text-xs text-gray-500">{merchant.threeds_rate}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="font-semibold text-red-800">-{merchant.processor_declined}</span>
                            <span className="text-xs text-gray-500">{merchant.processor_rate}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="font-semibold text-green-600">{merchant.approved}</div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className={`font-bold text-lg ${approvalColor}`}>
                            {merchant.approval_rate}%
                          </div>
                          <div className="text-xs text-gray-500">
                            {merchant.approved}/{merchant.total_transactions} txns
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className={`font-bold text-lg ${orderSuccessColor}`}>
                            {merchant.order_success_rate || 0}%
                          </div>
                          <div className="text-xs text-gray-500">
                            {merchant.approved_unique_orders || 0}/{merchant.total_unique_orders || 0} orders
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* Totals Row */}
                <tfoot>
                  {(() => {
                    const filteredData = merchantFunnelData.filter(merchant => {
                      const matchesSearch = !merchantTableFilters.searchText || 
                        merchant.merchant_name?.toLowerCase().includes(merchantTableFilters.searchText.toLowerCase());
                      const matchesMinRate = !merchantTableFilters.minRate || 
                        merchant.approval_rate >= parseFloat(merchantTableFilters.minRate);
                      const matchesMaxRate = !merchantTableFilters.maxRate || 
                        merchant.approval_rate <= parseFloat(merchantTableFilters.maxRate);
                      return matchesSearch && matchesMinRate && matchesMaxRate;
                    });
                    
                    const totalTxns = filteredData.reduce((sum, m) => sum + m.total_transactions, 0);
                    const totalApproved = filteredData.reduce((sum, m) => sum + m.approved, 0);
                    const totalRisk = filteredData.reduce((sum, m) => sum + m.risk_declined, 0);
                    const total3ds = filteredData.reduce((sum, m) => sum + m.threeds_declined, 0);
                    const totalProcessor = filteredData.reduce((sum, m) => sum + m.processor_declined, 0);
                    
                    return (
                      <tr className="bg-indigo-50 border-t-2 border-indigo-200 font-semibold">
                        <td className="py-3 px-4 text-sm text-indigo-700"></td>
                        <td className="py-3 px-4 text-indigo-700 font-bold">
                          TOTAL {filteredData.length < merchantFunnelData.length && `(filtered)`}
                        </td>
                        <td className="py-3 px-4 text-center text-indigo-900 font-bold">
                          {totalTxns.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-bold text-orange-600">
                            -{totalRisk.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-bold text-red-600">
                            -{total3ds.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-bold text-red-800">
                            -{totalProcessor.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-bold text-green-600">
                            {totalApproved.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-bold text-green-700">
                            {totalTxns > 0 ? ((totalApproved / totalTxns) * 100).toFixed(1) : 0}%
                          </span>
                          <div className="text-xs text-gray-500">
                            {totalApproved.toLocaleString()}/{totalTxns.toLocaleString()} txns
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-bold text-blue-600">
                            {(() => {
                              const totalUniqueOrders = filteredData.reduce((sum, m) => sum + (m.total_unique_orders || 0), 0);
                              const approvedUniqueOrders = filteredData.reduce((sum, m) => sum + (m.approved_unique_orders || 0), 0);
                              return totalUniqueOrders > 0 ? ((approvedUniqueOrders / totalUniqueOrders) * 100).toFixed(1) : 0;
                            })()}%
                          </span>
                          <div className="text-xs text-gray-500">
                            {filteredData.reduce((sum, m) => sum + (m.approved_unique_orders || 0), 0).toLocaleString()}/
                            {filteredData.reduce((sum, m) => sum + (m.total_unique_orders || 0), 0).toLocaleString()} orders
                          </div>
                        </td>
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
            </div>

            {/* Pagination — 50 merchants per page */}
            {filteredMerchantFunnel.length > MERCHANT_FUNNEL_PAGE_SIZE && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100" data-testid="merchant-funnel-pagination">
                <p className="text-sm text-gray-500">
                  Showing{' '}
                  <span className="font-medium text-gray-700">
                    {(merchantFunnelPage - 1) * MERCHANT_FUNNEL_PAGE_SIZE + 1}–{Math.min(merchantFunnelPage * MERCHANT_FUNNEL_PAGE_SIZE, filteredMerchantFunnel.length)}
                  </span>{' '}
                  of <span className="font-medium text-gray-700">{filteredMerchantFunnel.length.toLocaleString()}</span> merchants
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setMerchantFunnelPage(p => Math.max(1, p - 1))}
                    disabled={merchantFunnelPage <= 1}
                    data-testid="merchant-funnel-prev"
                  >
                    Prev
                  </Button>
                  {(() => {
                    const pages = [];
                    const total = merchantFunnelTotalPages;
                    const cur = merchantFunnelPage;
                    const win = 2;
                    let start = Math.max(1, cur - win);
                    let end = Math.min(total, cur + win);
                    if (start > 1) pages.push(1);
                    if (start > 2) pages.push('…l');
                    for (let i = start; i <= end; i++) pages.push(i);
                    if (end < total - 1) pages.push('…r');
                    if (end < total) pages.push(total);
                    return pages.map((p, idx) =>
                      typeof p === 'number' ? (
                        <Button
                          key={p}
                          variant={p === cur ? 'default' : 'outline'}
                          size="sm"
                          className={p === cur ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}
                          onClick={() => setMerchantFunnelPage(p)}
                          data-testid={`merchant-funnel-page-${p}`}
                        >
                          {p}
                        </Button>
                      ) : (
                        <span key={`${p}-${idx}`} className="px-1 text-gray-400">…</span>
                      )
                    );
                  })()}
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setMerchantFunnelPage(p => Math.min(merchantFunnelTotalPages, p + 1))}
                    disabled={merchantFunnelPage >= merchantFunnelTotalPages}
                    data-testid="merchant-funnel-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <span className="text-xs text-gray-600">Risk Declined</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-xs text-gray-600">3DS Failed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-800"></div>
                <span className="text-xs text-gray-600">Processor Declined</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-xs text-gray-600">Approved</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
        </>
      )}

      {/* Declined Reasons Tab Content */}
      {mainTab === 'declined_reasons' && (
        <>
          {/* Filters */}
          <Card className="border-0 shadow-md bg-white">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Period:</span>
                  <Select value={filters.period} onValueChange={(v) => handleFilterChange('period', v)}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {periodOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {filters.period === 'custom' && (
                    <>
                      <Select value={filters.customMonth?.toString() || ''} onValueChange={(v) => handleFilterChange('customMonth', parseInt(v))}>
                        <SelectTrigger className="w-32"><SelectValue placeholder="Month" /></SelectTrigger>
                        <SelectContent>
                          {monthOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={filters.customYear?.toString() || ''} onValueChange={(v) => handleFilterChange('customYear', parseInt(v))}>
                        <SelectTrigger className="w-24"><SelectValue placeholder="Year" /></SelectTrigger>
                        <SelectContent>
                          {yearOptions.map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                </div>
                {/* Channel Filter (moved next to Period) */}
                {renderChannelMultiSelect('analytics-declines-channel-filter')}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Country:</span>
                  <Select value={filters.country} onValueChange={(v) => handleFilterChange('country', v)}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {countries.filter(c => c && c.code).map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          <span className={`fi fi-${c.code.toLowerCase()} mr-2`}></span>
                          {c.name || getCountryName(c.code)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Currency:</span>
                  <Select value={filters.currency} onValueChange={(v) => handleFilterChange('currency', v)}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {currencyFilterOptions.map(c => (
                        <SelectItem key={c} value={c}>{formatCurrencySelector(c)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Processor:</span>
                  <Select value={filters.processor} onValueChange={(v) => handleFilterChange('processor', v)}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {processors.filter(p => p && typeof p === 'string').map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Merchant:</span>
                  <MerchantSearchSelect
                    merchants={merchants}
                    value={filters.merchant_id}
                    onChange={(v) => handleFilterChange('merchant_id', v)}
                    placeholder="All Merchants"
                    className="w-48"
                  />
                </div>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
                    <X className="h-4 w-4 mr-1" /> Clear
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={fetchDeclineTrends} disabled={declineLoading} className="flex items-center gap-2 ml-auto">
                  <RefreshCw className={`h-4 w-4 ${declineLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Line Chart */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BarChart3 className="h-5 w-5 text-indigo-600" />
                    Result Code % of Total Transactions by Day
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Each line represents a result code's share of daily transactions
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="show-approved-toggle">
                    <input
                      type="checkbox"
                      checked={showApproved}
                      onChange={(e) => setShowApproved(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-600">Show 00-Approved</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="show-declined-sum-toggle">
                    <input
                      type="checkbox"
                      checked={showDeclinedSum}
                      onChange={(e) => setShowDeclinedSum(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-sm text-gray-600">Show Declined Sum</span>
                  </label>
                  {declineData && declineData.result_codes?.length > 0 && !showDeclinedSum && (
                    <MultiSelect
                      options={
                        (declineData.result_codes || [])
                          .filter(code => showApproved || !APPROVED_CODES.includes(code))
                          .map(code => {
                            const desc = declineData.summary?.find(s => s.result_code === code)?.description;
                            return { code, name: desc ? `${code}: ${desc}` : code };
                          })
                      }
                      value={selectedCodes}
                      onChange={setSelectedCodes}
                      placeholder="All Series"
                      className="w-56"
                      maxDisplayItems={2}
                      renderOption={(opt) => opt.name || opt.code}
                      renderValue={(opt) => opt.name || opt.code}
                    />
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {declineLoading ? (
                <div className="h-80 flex items-center justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-indigo-400" />
                </div>
              ) : declineData && declineData.daily_data?.length > 0 ? (
                <DeclineTrendsChart
                  declineData={declineData}
                  showApproved={showApproved}
                  showDeclinedSum={showDeclinedSum}
                  selectedCodes={selectedCodes}
                />
              ) : (
                <div className="h-80 flex items-center justify-center">
                  <div className="text-center">
                    <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No decline data available for this period</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary Table */}
          {declineData && declineData.summary?.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">Result Code Breakdown</CardTitle>
                <p className="text-sm text-gray-500">
                  Total: {declineData.total_transactions?.toLocaleString()} transactions
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 text-left font-medium text-gray-600">#</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Code</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-600">Count</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-600">% of Total</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-600">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {declineData.summary.map((row, idx) => (
                        <tr key={row.result_code} className={`border-b border-gray-50 ${row.is_approved ? 'bg-green-50/50' : ''}`}>
                          <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold text-white ${row.is_approved ? 'bg-green-500' : 'bg-red-500'}`}>
                              {row.description ? `${row.result_code}: ${row.description}` : row.result_code}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">{row.count.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-medium ${row.is_approved ? 'text-green-600' : 'text-red-600'}`}>
                              {row.percentage}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">${row.total_amount.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Retry Waterfall Tab Content */}
      {mainTab === 'retry_waterfall' && (
        <>
          {/* Sub-tabs for channel */}
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Repeat className="h-5 w-5 text-indigo-600" />
                Retry Waterfall
              </h2>
              <p className="text-gray-500 text-sm">
                Track how many declined payments are retried and eventually recovered.
                Attempts are grouped by customer email + merchant + amount on the same day.
              </p>
            </div>
          </div>

          {/* Filters (same layout as Declined Reasons tab) */}
          <Card className="border-0 shadow-md bg-white">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Period:</span>
                  <Select value={filters.period} onValueChange={(v) => handleFilterChange('period', v)}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {periodOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {filters.period === 'custom' && (
                    <>
                      <Select value={filters.customMonth?.toString() || ''} onValueChange={(v) => handleFilterChange('customMonth', parseInt(v))}>
                        <SelectTrigger className="w-32"><SelectValue placeholder="Month" /></SelectTrigger>
                        <SelectContent>
                          {monthOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={filters.customYear?.toString() || ''} onValueChange={(v) => handleFilterChange('customYear', parseInt(v))}>
                        <SelectTrigger className="w-24"><SelectValue placeholder="Year" /></SelectTrigger>
                        <SelectContent>
                          {yearOptions.map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                </div>
                {/* Channel Filter (moved next to Period) */}
                {renderChannelMultiSelect('analytics-retries-channel-filter')}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Country:</span>
                  <Select value={filters.country} onValueChange={(v) => handleFilterChange('country', v)}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {countries.filter(c => c && c.code).map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          <span className={`fi fi-${c.code.toLowerCase()} mr-2`}></span>
                          {c.name || getCountryName(c.code)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Currency:</span>
                  <Select value={filters.currency} onValueChange={(v) => handleFilterChange('currency', v)}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {currencyFilterOptions.map(c => (
                        <SelectItem key={c} value={c}>{formatCurrencySelector(c)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Processor:</span>
                  <Select value={filters.processor} onValueChange={(v) => handleFilterChange('processor', v)}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {processors.filter(p => p && typeof p === 'string').map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Merchant:</span>
                  <MerchantSearchSelect
                    merchants={merchants}
                    value={filters.merchant_id}
                    onChange={(v) => handleFilterChange('merchant_id', v)}
                    placeholder="All Merchants"
                    className="w-48"
                  />
                </div>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
                    <X className="h-4 w-4 mr-1" /> Clear
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={fetchRetryWaterfall} disabled={retryLoading} className="flex items-center gap-2 ml-auto" data-testid="retry-refresh-btn">
                  <RefreshCw className={`h-4 w-4 ${retryLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          {retryLoading ? (
            <div className="h-40 flex items-center justify-center">
              <RefreshCw className="h-8 w-8 animate-spin text-indigo-400" />
            </div>
          ) : retryData && retryData.total_groups > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="retry-summary-cards">
                <Card className="border-0 shadow-md bg-gradient-to-br from-indigo-50 to-white">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-600">Payment Attempts</p>
                    <p className="text-xl font-bold text-indigo-700">{formatNumberForOverview(retryData.total_groups)}</p>
                    <p className="text-xs text-indigo-500 mt-1">unique (email+merchant+amt/day)</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-50 to-white border-l-4 border-l-emerald-500">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-600">First-Attempt Approved</p>
                    <p className="text-xl font-bold text-emerald-700">{formatNumberForOverview(retryData.first_approved)}</p>
                    <p className="text-xs text-emerald-600 mt-1">
                      {retryData.total_groups > 0 ? ((retryData.first_approved / retryData.total_groups) * 100).toFixed(1) : 0}% of total
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md bg-gradient-to-br from-red-50 to-white border-l-4 border-l-red-500">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-600">First-Attempt Declined</p>
                    <p className="text-xl font-bold text-red-700">{formatNumberForOverview(retryData.first_declined)}</p>
                    <p className="text-xs text-red-600 mt-1">
                      {retryData.total_groups > 0 ? ((retryData.first_declined / retryData.total_groups) * 100).toFixed(1) : 0}% of total
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-white">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-600">Retried</p>
                    <p className="text-xl font-bold text-amber-700">{formatNumberForOverview(retryData.retried)}</p>
                    <p className="text-xs text-amber-600 mt-1">{retryData.retry_rate}% of declined</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md bg-gradient-to-br from-green-50 to-white border-l-4 border-l-green-500">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-600">Recovered</p>
                    <p className="text-xl font-bold text-green-700">{formatNumberForOverview(retryData.retried_and_recovered)}</p>
                    <p className="text-xs text-green-600 mt-1">{retryData.recovery_rate}% recovery rate</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md bg-gradient-to-br from-rose-100 to-rose-50 border-l-4 border-l-rose-600">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-600">Still Declined</p>
                    <p className="text-xl font-bold text-rose-700">{formatNumberForOverview(retryData.retried_and_failed + retryData.single_attempt_declined)}</p>
                    <p className="text-xs text-rose-600 mt-1">never approved</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-blue-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-600">Final Approval Rate</p>
                    <p className="text-xl font-bold text-blue-700">{retryData.final_approval_rate}%</p>
                    <p className="text-xs text-blue-600 mt-1">first + recovered</p>
                  </CardContent>
                </Card>
              </div>

              {/* Waterfall Visualization */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ChevronDown className="h-5 w-5 text-indigo-600" />
                    Retry Waterfall
                  </CardTitle>
                  <p className="text-sm text-gray-500">
                    Flow from all attempts → declined → retried → recovered vs still declined
                  </p>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const stages = [
                      {
                        name: 'Payment Attempts',
                        value: retryData.total_groups,
                        color: '#6366f1',
                        sub: `${retryData.total_attempts.toLocaleString()} total transactions`,
                      },
                      {
                        name: 'First-Attempt Approved',
                        value: retryData.first_approved,
                        color: '#10b981',
                        sub: `${retryData.total_groups > 0 ? ((retryData.first_approved / retryData.total_groups) * 100).toFixed(1) : 0}% of attempts`,
                      },
                      {
                        name: 'First-Attempt Declined',
                        value: retryData.first_declined,
                        color: '#ef4444',
                        sub: `${retryData.total_groups > 0 ? ((retryData.first_declined / retryData.total_groups) * 100).toFixed(1) : 0}% of attempts`,
                      },
                      {
                        name: 'Retried',
                        value: retryData.retried,
                        color: '#f59e0b',
                        sub: `${retryData.retry_rate}% of declined`,
                      },
                      {
                        name: 'Recovered (Eventually Approved)',
                        value: retryData.retried_and_recovered,
                        color: '#059669',
                        sub: `${retryData.recovery_rate}% recovery rate`,
                      },
                      {
                        name: 'Still Declined After Retry',
                        value: retryData.retried_and_failed,
                        color: '#991b1b',
                        sub: retryData.retried > 0 ? `${((retryData.retried_and_failed / retryData.retried) * 100).toFixed(1)}% of retried` : '0%',
                      },
                    ];
                    const maxValue = stages[0].value || 1;
                    return (
                      <div className="space-y-2">
                        {stages.map((s) => {
                          const widthPct = Math.max((s.value / maxValue) * 100, s.value > 0 ? 8 : 2);
                          return (
                            <div key={s.name} className="flex items-center gap-3">
                              <div className="w-52 text-sm text-gray-700 font-medium text-right">
                                {s.name}
                              </div>
                              <div className="flex-1 relative h-10 bg-gray-50 rounded-md overflow-hidden">
                                <div
                                  className="h-full rounded-md flex items-center justify-between px-3 transition-all duration-500"
                                  style={{ width: `${widthPct}%`, backgroundColor: s.color }}
                                >
                                  <span className="text-white font-semibold text-sm">
                                    {formatNumberForOverview(s.value)}
                                  </span>
                                </div>
                              </div>
                              <div className="w-40 text-xs text-gray-500">{s.sub}</div>
                            </div>
                          );
                        })}
                        {retryData.revenue_recovered > 0 && (
                          <div className="pt-3 mt-3 border-t border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <TrendingUp className="h-4 w-4 text-green-600" />
                              Revenue Recovered by Retries
                            </div>
                            <div className="text-lg font-bold text-green-700">
                              ${retryData.revenue_recovered.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Attempts Distribution */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">Attempts per Payment Distribution</CardTitle>
                  <p className="text-sm text-gray-500">How many attempts each unique payment needed</p>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={Object.entries(retryData.attempts_distribution || {}).map(([k, v]) => ({
                          attempts: k === '1' ? '1 attempt' : k === '4+' ? '4+ attempts' : `${k} attempts`,
                          count: v,
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="attempts" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} isAnimationActive={!isExporting} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Merchants Table */}
              {retryData.merchants && retryData.merchants.length > 0 && (
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-indigo-600" />
                      Retry Recovery by Merchant
                      <Badge variant="outline" className="ml-2 text-xs">
                        Top {retryData.merchants.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b-2 border-gray-200">
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">#</th>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Merchant</th>
                            <th className="text-center py-3 px-4 font-semibold text-gray-700">Attempts</th>
                            <th className="text-center py-3 px-4 font-semibold text-red-600">First Declined</th>
                            <th className="text-center py-3 px-4 font-semibold text-amber-600">Retried</th>
                            <th className="text-center py-3 px-4 font-semibold text-amber-600">Retry Rate</th>
                            <th className="text-center py-3 px-4 font-semibold text-green-600">Recovered</th>
                            <th className="text-center py-3 px-4 font-semibold text-green-600">Recovery Rate</th>
                            <th className="text-right py-3 px-4 font-semibold text-green-600">Revenue Recovered</th>
                          </tr>
                        </thead>
                        <tbody>
                          {retryData.merchants.map((m, idx) => {
                            const rowBg = idx % 2 === 0 ? 'bg-gray-50/50' : 'bg-white';
                            const recColor = m.recovery_rate >= 20 ? 'text-green-600'
                              : m.recovery_rate >= 10 ? 'text-amber-600'
                              : 'text-red-600';
                            return (
                              <tr key={m.merchant_id} className={`${rowBg} hover:bg-indigo-50/50 transition-colors border-b border-gray-100`} data-testid={`retry-merchant-row-${idx}`}>
                                <td className="py-3 px-4 text-gray-500">{idx + 1}</td>
                                <td className="py-3 px-4 font-medium text-gray-900">{m.merchant_name}</td>
                                <td className="py-3 px-4 text-center font-semibold text-gray-900">{m.total_groups}</td>
                                <td className="py-3 px-4 text-center text-red-600 font-semibold">{m.first_declined}</td>
                                <td className="py-3 px-4 text-center text-amber-600 font-semibold">{m.retried}</td>
                                <td className="py-3 px-4 text-center text-amber-600">{m.retry_rate}%</td>
                                <td className="py-3 px-4 text-center text-green-600 font-semibold">{m.recovered}</td>
                                <td className={`py-3 px-4 text-center font-bold ${recColor}`}>{m.recovery_rate}%</td>
                                <td className="py-3 px-4 text-right text-green-700 font-semibold">
                                  ${m.revenue_recovered.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-0 shadow-lg">
              <CardContent className="h-60 flex flex-col items-center justify-center">
                <Repeat className="h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-500">No retry data available for the selected period</p>
                <p className="text-sm text-gray-400 mt-1">Try expanding the date range or clearing filters</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Merchants Tab Content */}
      {mainTab === 'merchant_growth' && (
        <div className="space-y-6">
        {/* Shared filter bar — applies to New/Inactive merchant lists and the Pareto chart */}
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Period:</span>
                <Select value={filters.period} onValueChange={(v) => handleFilterChange('period', v)}>
                  <SelectTrigger className="w-36" data-testid="merchants-period-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {periodOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {filters.period === 'custom' && (
                  <>
                    <Select value={filters.customMonth?.toString() || ''} onValueChange={(v) => handleFilterChange('customMonth', parseInt(v))}>
                      <SelectTrigger className="w-32"><SelectValue placeholder="Month" /></SelectTrigger>
                      <SelectContent>
                        {monthOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={filters.customYear?.toString() || ''} onValueChange={(v) => handleFilterChange('customYear', parseInt(v))}>
                      <SelectTrigger className="w-24"><SelectValue placeholder="Year" /></SelectTrigger>
                      <SelectContent>
                        {yearOptions.map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Country:</span>
                <Select value={filters.country} onValueChange={(v) => handleFilterChange('country', v)}>
                  <SelectTrigger className="w-40" data-testid="merchants-country-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countries.filter(c => c && c.code).map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        <span className={`fi fi-${c.code.toLowerCase()} mr-2`}></span>
                        {c.name || getCountryName(c.code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Currency:</span>
                <Select value={filters.currency} onValueChange={(v) => handleFilterChange('currency', v)}>
                  <SelectTrigger className="w-32" data-testid="merchants-currency-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {currencyFilterOptions.map(c => (
                      <SelectItem key={c} value={c}>{formatCurrencySelector(c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Processor:</span>
                <Select value={filters.processor} onValueChange={(v) => handleFilterChange('processor', v)}>
                  <SelectTrigger className="w-36" data-testid="merchants-processor-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {processors.filter(p => p && typeof p === 'string').map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Merchant:</span>
                <MerchantSearchSelect
                  merchants={merchants}
                  value={filters.merchant_id}
                  onChange={(v) => handleFilterChange('merchant_id', v)}
                  placeholder="All Merchants"
                  className="w-48"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500" data-testid="merchants-clear-filters">
                  <X className="h-4 w-4 mr-1" /> Clear
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Country / Currency / Processor / Merchant filters apply to the New &amp; Inactive merchant lists and the Pareto chart below. Period applies to the Pareto chart.
            </p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5 text-indigo-600" />
              Active Merchants by Month
            </CardTitle>
            <p className="text-sm text-gray-500">Merchants with at least one transaction per month</p>
          </CardHeader>
          <CardContent>
            {merchantsLoading ? (
              <div className="h-80 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
                  <p className="text-gray-500">Loading merchant data...</p>
                </div>
              </div>
            ) : merchantsByMonth.length > 0 ? (
              <>
                {/* Area Chart */}
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={merchantsByMonth} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      <defs>
                        <linearGradient id="colorMerchants" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="month_short" 
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                      <YAxis 
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: '#d1d5db' }}
                        tickFormatter={(value) => value.toLocaleString()}
                      />
                      <Tooltip 
                        formatter={(value) => [value.toLocaleString(), 'Active Merchants']}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.month || label}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Area 
                        type="monotone"
                        dataKey="active_merchants" 
                        stroke="#6366f1"
                        strokeWidth={2}
                        fill="url(#colorMerchants)"
                        name="Active Merchants"
                        isAnimationActive={!isExporting}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Data Table */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Monthly Breakdown</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          {merchantsByMonth.map((item) => (
                            <th key={item.month} className="px-2 py-2 text-center font-medium text-gray-600">
                              {item.month_short}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {merchantsByMonth.map((item) => (
                            <td key={item.month} className="px-2 py-2 text-center text-gray-800">
                              {item.active_merchants.toLocaleString()}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          {merchantsByMonth.map((item, idx) => {
                            const prev = idx > 0 ? merchantsByMonth[idx - 1].active_merchants : 0;
                            const curr = item.active_merchants;
                            if (prev === 0 && curr === 0) {
                              return <td key={item.month} className="px-2 py-1 text-center text-xs text-gray-400">-</td>;
                            }
                            if (prev === 0 && curr > 0) {
                              return <td key={item.month} className="px-2 py-1 text-center text-xs font-medium text-green-600">new</td>;
                            }
                            const pctChange = ((curr - prev) / prev * 100).toFixed(1);
                            const isPositive = curr >= prev;
                            return (
                              <td key={item.month} className={`px-2 py-1 text-center text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                                {isPositive ? '+' : ''}{pctChange}%
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-600">
                      {merchantsByMonth[merchantsByMonth.length - 1]?.active_merchants?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-500">This Month</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {Math.max(...merchantsByMonth.map(m => m.active_merchants || 0)).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">Peak (12 months)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-700">
                      {(() => {
                        const activeMonths = merchantsByMonth.filter(m => (m.active_merchants || 0) > 0);
                        if (activeMonths.length === 0) return '0';
                        return Math.round(activeMonths.reduce((sum, m) => sum + m.active_merchants, 0) / activeMonths.length).toLocaleString();
                      })()}
                    </p>
                    <p className="text-sm text-gray-500">Avg Monthly</p>
                  </div>
                </div>

                {/* Merchant Movement Table */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Merchant Movement</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Year</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Month</th>
                          <th className="px-3 py-2 text-right font-medium text-green-600">New</th>
                          <th className="px-3 py-2 text-right font-medium text-red-500">Inactive</th>
                          <th className="px-3 py-2 text-right font-medium text-blue-600">Returning</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {merchantsByMonth
                          .filter(m => m.active_merchants > 0 || m.new_merchants > 0 || m.inactive_merchants > 0)
                          .map((item, idx) => (
                          <tr key={item.month} className={`border-b border-gray-100 ${idx % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                            <td className="px-3 py-2 text-gray-600">{item.year}</td>
                            <td className="px-3 py-2 text-gray-800 font-medium">{item.month_short}</td>
                            <td className="px-3 py-2 text-right">
                              {item.new_merchants > 0 ? (
                                <span className="text-green-600 font-medium">+{item.new_merchants}</span>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {item.inactive_merchants > 0 ? (
                                <span className="text-red-500 font-medium">-{item.inactive_merchants}</span>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {item.returning_merchants > 0 ? (
                                <span className="text-blue-600 font-medium">{item.returning_merchants}</span>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-gray-800">{item.active_merchants}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    New = merchants with activity this month but not last month. 
                    Inactive = merchants with activity last month but not this month. 
                    Returning = merchants active both months.
                  </p>
                </div>

                {/* Top New and Inactive Merchants */}
                {(topNewMerchants.length > 0 || topInactiveMerchants.length > 0) && (
                  <div className="mt-6 pt-6 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Top New Merchants */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-green-700 flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          Top 10 New Merchants
                        </h4>
                        <Select value={newMerchantSort} onValueChange={setNewMerchantSort}>
                          <SelectTrigger className="w-44 h-8 text-xs" data-testid="top-new-sort-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="revenue">Sort: Revenue ↓</SelectItem>
                            <SelectItem value="transactions">Sort: Transactions ↓</SelectItem>
                            <SelectItem value="approved">Sort: Approved count ↓</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {sortedTopNew.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="border-b border-gray-200 bg-green-50/50">
                                <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Merchant</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Txns</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Approved</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedTopNew.map((m, idx) => (
                                <tr key={m.merchant_id} className="border-b border-gray-50">
                                  <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                                  <td className="px-3 py-2 text-gray-800 font-medium truncate max-w-[180px]">{m.name}</td>
                                  <td className="px-3 py-2 text-right text-green-600 font-medium">{m.transactions.toLocaleString()}</td>
                                  <td className="px-3 py-2 text-right text-gray-700">{(m.approved || 0).toLocaleString()}</td>
                                  <td className="px-3 py-2 text-right text-gray-700">{merchantsCurrencySym}{(m.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No new merchants this month</p>
                      )}
                    </div>

                    {/* Top Inactive Merchants */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-red-600 flex items-center gap-2">
                          <TrendingDown className="h-4 w-4" />
                          Top 10 Inactive Merchants
                        </h4>
                        <Select value={inactiveMerchantSort} onValueChange={setInactiveMerchantSort}>
                          <SelectTrigger className="w-44 h-8 text-xs" data-testid="top-inactive-sort-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="last_revenue">Sort: Revenue ↓</SelectItem>
                            <SelectItem value="last_transactions">Sort: Transactions ↓</SelectItem>
                            <SelectItem value="last_approved">Sort: Approved count ↓</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {sortedTopInactive.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="border-b border-gray-200 bg-red-50/50">
                                <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Merchant</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Last Txns</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Last Approved</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Last Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedTopInactive.map((m, idx) => (
                                <tr key={m.merchant_id} className="border-b border-gray-50">
                                  <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                                  <td className="px-3 py-2 text-gray-800 font-medium truncate max-w-[180px]">{m.name}</td>
                                  <td className="px-3 py-2 text-right text-red-500 font-medium">{m.last_transactions.toLocaleString()}</td>
                                  <td className="px-3 py-2 text-right text-gray-700">{(m.last_approved || 0).toLocaleString()}</td>
                                  <td className="px-3 py-2 text-right text-gray-700">{merchantsCurrencySym}{(m.last_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No inactive merchants this month</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No merchant data available</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Merchant Pareto Analysis Card */}
        <Card className="border-0 shadow-lg" data-testid="merchant-pareto-card">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="h-5 w-5 text-indigo-600" />
                  Merchant Pareto Analysis
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Revenue concentration across merchants — Top 20 + Others, with cumulative % (80/20 rule). Approved transactions only.
                </p>
              </div>
              {/* Metric toggle */}
              <Tabs value={paretoMetric} onValueChange={setParetoMetric} className="w-auto">
                <TabsList className="bg-gray-100">
                  <TabsTrigger value="revenue" className="data-[state=active]:bg-white" data-testid="pareto-metric-revenue">Revenue</TabsTrigger>
                  <TabsTrigger value="transactions" className="data-[state=active]:bg-white" data-testid="pareto-metric-transactions">Transactions</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            {paretoLoading ? (
              <div className="h-80 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
                  <p className="text-gray-500">Loading Pareto data...</p>
                </div>
              </div>
            ) : paretoChartData.length > 0 ? (
              (() => {
                const isRevenue = paretoMetric === 'revenue';
                const block = paretoData?.[paretoMetric] || {};
                const sym = isRevenue ? getCurrencySymbol(paretoData?.currency || 'USD') : '';
                const curCode = paretoData?.is_usd_equivalent ? 'USD' : (paretoData?.currency || 'USD');
                const realItems = paretoChartData.filter(d => d.merchant_id !== '__others__');
                const top20Cum = realItems.length ? realItems[realItems.length - 1].cumulative_pct : 0;
                const fmtVal = (v) => isRevenue
                  ? `${sym}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                  : Number(v).toLocaleString();
                const fmtAxis = (v) => {
                  if (!isRevenue) return v.toLocaleString();
                  if (v >= 1e6) return `${sym}${(v / 1e6).toFixed(1)}M`;
                  if (v >= 1e3) return `${sym}${(v / 1e3).toFixed(0)}k`;
                  return `${sym}${v}`;
                };
                return (
                  <>
                    {/* Summary line */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      <div className="rounded-lg bg-indigo-50 p-4">
                        <p className="text-2xl font-bold text-indigo-600" data-testid="pareto-total">{fmtVal(block.total || 0)}</p>
                        <p className="text-sm text-gray-500">Total {isRevenue ? `${curCode} ${paretoData?.is_usd_equivalent ? '(USD eq.)' : ''}` : 'Transactions'}</p>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-4">
                        <p className="text-2xl font-bold text-gray-700">{(block.merchant_count || 0).toLocaleString()}</p>
                        <p className="text-sm text-gray-500">Active Merchants</p>
                      </div>
                      <div className="rounded-lg bg-amber-50 p-4">
                        <p className="text-2xl font-bold text-amber-600">{top20Cum.toFixed(1)}%</p>
                        <p className="text-sm text-gray-500">Top {realItems.length} share of {isRevenue ? 'revenue' : 'transactions'}</p>
                      </div>
                    </div>

                    {/* Pareto Chart */}
                    <div className="h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={paretoChartData} margin={{ top: 20, right: 40, left: 20, bottom: 70 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey="label"
                            tick={{ fill: '#6b7280', fontSize: 11 }}
                            axisLine={{ stroke: '#d1d5db' }}
                            interval={0}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis
                            yAxisId="left"
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            axisLine={{ stroke: '#d1d5db' }}
                            tickFormatter={fmtAxis}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={[0, 100]}
                            ticks={[0, 20, 40, 60, 80, 100]}
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            axisLine={{ stroke: '#d1d5db' }}
                            tickFormatter={(v) => `${v}%`}
                          />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload || !payload.length) return null;
                              const d = payload[0].payload;
                              return (
                                <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100 text-sm">
                                  <p className="font-semibold text-gray-900 mb-1 max-w-[240px]">{d.name}</p>
                                  <p className="text-gray-600">{isRevenue ? 'Revenue' : 'Transactions'}: <span className="font-medium text-indigo-600">{fmtVal(d.value)}</span></p>
                                  <p className="text-gray-600">Share: <span className="font-medium">{d.pct}%</span></p>
                                  <p className="text-gray-600">Cumulative: <span className="font-medium text-amber-600">{d.cumulative_pct}%</span></p>
                                </div>
                              );
                            }}
                          />
                          <Legend verticalAlign="top" height={32} />
                          <Bar yAxisId="left" dataKey="value" name={isRevenue ? `Revenue (${curCode})` : 'Transactions'} fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={42} isAnimationActive={!isExporting}>
                            {paretoChartData.map((entry) => (
                              <Cell key={entry.merchant_id} fill={entry.merchant_id === '__others__' ? '#cbd5e1' : '#6366f1'} />
                            ))}
                          </Bar>
                          <Line yAxisId="right" type="monotone" dataKey="cumulative_pct" name="Cumulative %" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: '#f59e0b' }} isAnimationActive={!isExporting} />
                          <ReferenceLine yAxisId="right" y={80} stroke="#ef4444" strokeDasharray="6 4" label={{ value: '80%', position: 'right', fill: '#ef4444', fontSize: 12 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Detailed table */}
                    <div className="mt-6 pt-6 border-t border-gray-100 overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Merchant</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">{isRevenue ? `Revenue (${curCode})` : 'Transactions'}</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">Share</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">Cumulative</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paretoChartData.map((d, idx) => (
                            <tr key={d.merchant_id} className={`border-b border-gray-50 ${d.merchant_id === '__others__' ? 'bg-gray-50/60 italic' : ''}`}>
                              <td className="px-3 py-2 text-gray-400">{d.merchant_id === '__others__' ? '' : idx + 1}</td>
                              <td className="px-3 py-2 text-gray-800 font-medium truncate max-w-[260px]">{d.name}</td>
                              <td className="px-3 py-2 text-right text-gray-700">{fmtVal(d.value)}</td>
                              <td className="px-3 py-2 text-right text-gray-600">{d.pct}%</td>
                              <td className="px-3 py-2 text-right text-amber-600 font-medium">{d.cumulative_pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()
            ) : (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No merchant data available for the selected filters</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Customers Tab Content */}
      {mainTab === 'customers' && (
        <AnalyticsCustomers
          filters={filters}
          handleFilterChange={handleFilterChange}
          clearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          countries={countries}
          currencies={currencies}
          processors={processors}
          merchants={merchants}
          periodOptions={periodOptions}
          monthOptions={monthOptions}
          yearOptions={yearOptions}
          isExporting={isExporting}
        />
      )}
      </div>
    </div>
  );
};

export default Analytics;
